import React from "react";
import clsx from "clsx";
import { IFCParserChoices, PlaceDetailsOUT } from "../api-client";
import { StepIconProps } from "@material-ui/core";
import { globalStore } from "store/GlobalStore";
import { BGItem } from "components/ButtonGroups";

export function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

export const REQUIREMENTS_URL =
  "https://docs.circularlca.de/IFC-Modellanforderungen-27ac3a4b5a1d469c8b9657d84ffced68";

export const IFC_ENTITIES_URL =
  "https://docs.circularlca.de/Werden-bestimmte-IFC-Entit-ten-beim-Import-vom-System-ignoriert-a14fcb3ea9564e09967115016f18f2c0";

export const QNG_SUPPORTING_DOCS =
  "https://www.qng.info/wp-content/uploads/2024/07/QNG_Handbuch_Anlage-1-Siegelvarianten_v1-5.pdf";

export const LEVELS_DOC_URL =
  "https://susproc.jrc.ec.europa.eu/product-bureau/product-groups/412/documents";

export const CIRCULARITY_GUIDE_URL =
  "https://docs.circularlca.de/Circularity-Performance-Index-CPX-8aa764b1b29344eabf96466f68c23ed8";

export const BNB_DOCUMENT =
  "https://www.nachhaltigesbauen.de/fileadmin/pdf/Nutzungsdauer_Bauteile/BNB_Nutzungsdauern_von_Bauteilen_2017-02-24.pdf";

export const RESIDENTIAL_BUILDING_FILE =
  "https://concular-retool-assets.s3.eu-central-1.amazonaws.com/concore/b6_operational_energy/Betriebsenergie%2BBerechnung_Wohngeb%C3%A4ude.xlsx";

export const NON_RESIDENTIAL_BUILDING_FILE =
  "https://concular-retool-assets.s3.eu-central-1.amazonaws.com/concore/b6_operational_energy/Betriebsenergie%2BBerechnung_nichtwohngeb%C3%A4ude.xlsx";

export const RULES_USER_DOCS_URL =
  "https://docs.circularlca.de/Wie-funktioniert-das-regel-basierte-Verkn-pfen-1f36f0c6558c80169a22d5f53e2d06d1";

export const PORTFOLIO_URL = "portfolio-dashboard";
export const URBAN_MINING_HUB_URL = "urban-mining";
export const VARIANT_COMPARISON_DOCS =
  "https://docs.circularlca.de/Wie-funktioniert-der-Variantenvergleich-1856f0c6558c8038a31fe2034de9d71e";
export const HOTSPOT_ANALYZER_DOCS =
  "https://docs.circularlca.de/Wie-funktioniert-der-CO-Hotspot-Analyzer-1cb6f0c6558c807d87d0e3946ea78437";

export const CERTIFICATION_EXPORT_DOC_URL =
  "https://docs.circularlca.de/kobilanzierung-f-r-Zertifizierungen-0fac08a5324b495b853952a493308b95";

export const RELEASE_NOTES_URL =
  "https://docs.circularlca.de/Release-Notes-ffafd1621deb43fdb244a7848df3337e";

export const placeDetailsToString = (item: PlaceDetailsOUT) => {
  return item.street ||
    item.latitude ||
    item.longitude ||
    item.country_code ||
    item.place_id
    ? `${item.street ?? ""} ${item.house_number ?? ""} ${
        item.postal_code ?? ""
      } ${item.region ?? ""} ${item.country_code ?? ""}`.trim()
    : "";
};

export const isMobileSize = () => window.innerWidth <= 640;
export const isTabletSize = () => window.innerWidth <= 768;

export function slugify(str: string | number, separator = "-") {
  return str
    .toString()
    .normalize("NFD")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, separator);
}

export default function checkEmailValidation(value: string) {
  return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value);
}

export function skeleton(className?: string) {
  return (
    <div className="animate-pulse">
      <div
        className={`h-80 w-full bg-gray-200 rounded-lg dark:bg-gray-700 mb-2.5 ${className}`}
      ></div>
    </div>
  );
}

export const StepperCustomCircle = (props: StepIconProps) => {
  return (
    <div
      className={clsx(
        "flex rounded-full w-8 h-8 justify-center items-center self-center",
        {
          "border border-indigo-600 text-indigo-700": props.active,
          "bg-indigo-600 border border-indigo-600 text-white": props.completed,
          "bg-gray-100 border border-gray-100 text-gray-500":
            !props.completed && !props.active,
        }
      )}
    >
      {props.icon}
    </div>
  );
};

export const unitPower = (unit: string) => {
  return (
    <span>
      {unit.endsWith("3") || unit.endsWith("2") ? (
        <>
          {unit.slice(0, unit.length - 1)}
          <sup>{unit[unit.length - 1]}</sup>
        </>
      ) : (
        unit
      )}
    </span>
  );
};

export const numberCustomFormat = (num: number) => {
  if (num === 0) {
    return "0.00";
  } else if (num < 0.01) {
    return num.toFixed(6).replace(/0+$/, "");
  } else {
    return num.toFixed(2);
  }
};

/**
 * Check the status if it's undefined or null and includes failed, return true
 */
export function isFailedStatus(status: string | null | undefined) {
  return status?.includes("failed") ?? false;
}

export function getModuleStringTotal(certification: string | undefined | null) {
  if (certification === "DGNB 2018") {
    return "moduleString_total_2018";
  } else if (certification?.includes("Level")) {
    return "moduleString_levels";
  } else {
    return "moduleString_total";
  }
}

export function getModuleStringB4(certification: string | undefined) {
  if (certification === "DGNB 2018") {
    return "moduleString_b4_2018";
  } else {
    return "moduleString_b4";
  }
}

export function roundNumber(num: number | string) {
  return Math.round((Number(num) + Number.EPSILON) * 100) / 100;
}

export function formatFloatNumber(num: number) {
  return num.toLocaleString("de-DE", { maximumFractionDigits: 2 });
}

export const IFC_CLASSES = [
  { id: "IfcBeam", name: "IfcBeam" },
  { id: "IfcBearing", name: "IfcBearing" },
  { id: "IfcBuildingElementProxy", name: "IfcBuildingElementProxy" },
  { id: "IfcChimney", name: "IfcChimney" },
  { id: "IfcColumn", name: "IfcColumn" },
  { id: "IfcCovering", name: "IfcCovering" },
  { id: "IfcCurtainWall", name: "IfcCurtainWall" },
  { id: "IfcDeepFoundation", name: "IfcDeepFoundation" },
  { id: "IfcDoor", name: "IfcDoor" },
  { id: "IfcFooting", name: "IfcFooting" },
  { id: "IfcMember", name: "IfcMember" },
  { id: "IfcPlate", name: "IfcPlate" },
  { id: "IfcRailing", name: "IfcRailing" },
  { id: "IcRamp", name: "IcRamp" },
  { id: "IfcRampFlight", name: "IfcRampFlight" },
  { id: "IfcRoof", name: "IfcRoof" },
  { id: "IfcShadingDevice", name: "IfcShadingDevice" },
  { id: "IfcSlab", name: "IfcSlab" },
  { id: "IfcStair", name: "IfcStair" },
  { id: "IfcStairFlight", name: "IfcStairFlight" },
  { id: "IfcWall", name: "IfcWall" },
  { id: "IfcWindow", name: "IfcWindow" },
];

const viteBuildName = import.meta.env.VITE_BUILD_NAME;
export const IS_CONCULAR_ONE =
  viteBuildName !== undefined && viteBuildName.includes("concular-one");

export const IS_CIRCULAR_LCA =
  viteBuildName === "" || viteBuildName === undefined;

export const IS_CIRCULAR_ASSET =
  viteBuildName !== undefined && viteBuildName.includes("circular-asset");

export const CONCULAR_ONE_URL = "https://www.concular.one/";

export const ORG_ACCORDION_STATUS_KEY = "org-accordion-status";

export const CONSULTATION_CALENDAR_URL =
  "https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ1QdAbDcf1q0RlSYqQmH7bLlK99A8PZut2CVGDEVpyrr8COJeyIPMCHfLF42G9Ph8Uzg1wvBToj";
export const REQUEST_OFFER_URL = "https://concular.de/angebot-anfordern/";

export const SOFTWARE_EMAIL = "software@concular.com";
export const SUPPORT_EMAIL = "support@concular.com";
export const PRICES_OVERVIEW_URL = "https://circularlca.de/preise/";

export const BOOLEAN_OPTIONS = [
  {
    id: "true",
    name: "True",
    name_de: "Ja",
    value: true,
  },
  {
    id: "false",
    name: "False",
    name_de: "Nein",
    value: false,
  },
];

export const getBooleanName = (value: null | boolean) => {
  const foundedItem = BOOLEAN_OPTIONS.find((item) => item.value == value);
  return globalStore.currentLanguage === "de"
    ? foundedItem?.name_de
    : foundedItem?.name;
};

export const IconBricks = (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2 13.5882L7.64103 15.7059M2 13.5882V18.8824L7.64103 21M2 13.5882L9.57234 10.6935M7.64103 15.7059V21M7.64103 15.7059L11.3142 14.2899M7.64103 21L13.267 18.3219M9.57234 10.6935L11.3142 14.2899M9.57234 10.6935L7.64102 6.70588L10.2051 3M11.3142 14.2899L13.267 18.3219M13.267 18.3219L14.3077 20.4706M14.3077 20.4706L17.3846 17.8235M14.3077 20.4706L19.4359 18.8824L22 16.2353M10.2051 3L17.3846 17.8235M10.2051 3H15.8462L22 16.2353M17.3846 17.8235L22 16.2353"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const IconHomes = (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M11.1111 21C10.6986 21 10.3029 20.8361 10.0112 20.5444C9.71944 20.2527 9.55556 19.857 9.55556 19.4444V13C8.94807 13 8.60748 13 8 13L14.5 6.5M11.1111 21H16H18.8889C19.3014 21 19.6971 20.8361 19.9888 20.5444C20.2806 20.2527 20.4444 19.857 20.4444 19.4444V13H22L15 6L14.5 6.5M11.1111 21H6C5.46957 21 4.96086 20.7893 4.58579 20.4142C4.21071 20.0391 4 19.5304 4 19V12H2L11 3L14.5 6.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const IconProductManagement = (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M20 7.5L12 3L4 7.5M20 7.5V16.5L12 21M20 7.5L12 12M12 21L4 16.5V12M12 21V16.5M4 7.5L12 12M4 7.5V12M12 12V16.5M4 12L12 16.5M12 16.5L20 12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const IconProductMapping = (
  <svg
    width="24"
    height="24"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M14.6667 5.66671L10.6667 3.33337L6.66671 5.66671M14.6667 5.66671V10.3334L10.6667 12.6667L6.66671 10.3334V8.00004M14.6667 5.66671L10.6667 8.00004L6.66671 5.66671M6.66671 5.66671V8.00004M6.66671 8.00004L10.6667 10.3334L14.6667 8.00004M8.01379 4.88091L5.33337 3.33337L1.33337 5.66671V10.3334L5.33337 12.6667L8.01379 11.1192"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const USER_DOCS_URL = "https://docs.circularlca.de/";

export const areListsEqualUnordered = (list1: string[], list2: string[]) => {
  if (list1.length !== list2.length) return false;
  const sortedA = [...list1].sort((a, b) => a.localeCompare(b));
  const sortedB = [...list2].sort((a, b) => a.localeCompare(b));
  return sortedA.every((val, idx) => val === sortedB[idx]);
};

export const IconObject = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10 2.5L16.6667 6.25V13.75L10 17.5L3.33337 13.75V6.25L10 2.5Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const IconProduct = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M16.6667 6.25L10 2.5L3.33337 6.25M16.6667 6.25V13.75L10 17.5L3.33337 13.75V10M16.6667 6.25L10 10L3.33337 6.25M3.33337 6.25V10M3.33337 10L10 13.75L16.6667 10"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export function getPercentageStatusColor(percentage: number) {
  if (percentage >= 75) {
    return "#10B981";
  } else if (percentage >= 25) {
    return "#eab308";
  } else {
    return "#EF4444";
  }
}

export const localStorageKeys = {
  DEFAULT_ORGANISATION_KEY: "default_organisation",
  SHARED_LAST_CLICKED_BUILDING: "shared_last_clicked_building",
};

export const BUILDING_TYPES: BGItem[] = [
  {
    id: "residential-building",
    name: "Residential building",
    name_de: "Wohngebäude",
  },
  {
    id: "non-residential-building",
    name: "Non-residential building",
    name_de: "Nicht-Wohngebäude",
  },
];

export const IFC_PARSER_CHOICES: BGItem[] = [
  {
    id: IFCParserChoices.V20240,
    name: "Version 1",
    name_de: "Version 1",
  },
  {
    id: IFCParserChoices.V20250,
    name: "Version 2",
    name_de: "Version 2",
  },
];

export const LEVEL_OF_REQUIREMENTS: BGItem[] = [
  {
    id: "residential-building",
    name: "PLUS",
    name_de: "PLUS",
  },
  {
    id: "non-residential-building",
    name: "PREMIUM",
    name_de: "PREMIUM",
  },
];

export const ENERGETICS_STANDARDS: BGItem[] = [
  {
    id: "residential_under_40",
    name: "≤ 40 QP in % of QP Ref",
    name_de: "≤ 40 QP in % von QP Ref",
  },
  {
    id: "residential_over_40",
    name: "> 40 QP in % of QP Ref",
    name_de: "> 40 QP in % von QP Ref",
  },
];

export const BUILDING_CLASSES = [
  { id: "1", name: "K1" },
  { id: "2", name: "K2" },
  { id: "3", name: "K3" },
  { id: "4", name: "K4" },
  { id: "5", name: "K5" },
  { id: "6", name: "K6" },
];

export const CERTIFICATIONS_CALCULATION_LOGIC: {
  [key: string]: {
    modules: string;
    databases: { name: string; name_de: string };
    conformity: string;
    referenceArea: { name: string; name_de: string };
  };
} = {
  bnb: {
    modules: "A1-A3, B4, C3 und C4",
    databases: { name: "Ökobaudat 2016", name_de: "Ökobaudat 2016" },
    conformity: "DIN EN 15804 + A1",
    referenceArea: {
      name: "Net floor area",
      name_de: "Nettogrundfläche",
    },
  },
  dgnb18: {
    modules: "A1-A3, B4, C3, C4 und D",
    databases: {
      name: "Ökobaudat 2024, Weitere konforme EPD",
      name_de: "Ökobaudat 2024, weitere konforme EPD",
    },
    conformity: "DIN EN 15804 + A1",
    referenceArea: {
      name: "DGNB LCA reference area",
      name_de: "DGNB LCA Bezugsfläche",
    },
  },
  dgnb23: {
    modules: "A1-A3, B4, C3 und C4",
    databases: {
      name: "Ökobaudat 2024, Weitere konforme EPD",
      name_de: "Ökobaudat 2024, weitere konforme EPD",
    },
    conformity: "DIN EN 15804 + A2",
    referenceArea: {
      name: "Net room area",
      name_de: "Nettoraumfläche",
    },
  },
  levels: {
    modules: "A1-A3, C3 und C4",
    databases: { name: "Alle konformen EPD", name_de: "Alle konformen EPD" },
    conformity: "DIN EN 15804 + A2",
    referenceArea: {
      name: "Net room area",
      name_de: "Nettoraumfläche",
    },
  },
  qng: {
    modules: "A1-A3, B4, C3 und C4",
    databases: {
      name: "QNG Rechenwerttabelle",
      name_de: "QNG Rechenwerttabelle",
    },
    conformity: "DIN EN 15804 + A1",
    referenceArea: {
      name: "Net room area",
      name_de: "Nettoraumfläche",
    },
  },
};

export const CertificationEnum: { [key: string]: string } = {
  bnb: "BNB",
  dgnb18: "DGNB 2018",
  dgnb23: "DGNB 2023",
  levels: "Level(s)",
  qng: "QNG",
};

/** Inverse of CertificationEnum (name -> settings key). Static, built once. */
const CERTIFICATION_NAME_TO_KEY = new Map(
  Object.entries(CertificationEnum).map(([key, name]) => [name, key])
);

/** Maps a certification name (e.g. "DGNB 2023") to its settings key (e.g. "dgnb23"). */
export const certificationNameToKey = (
  name: string | null | undefined
): string | undefined =>
  name ? CERTIFICATION_NAME_TO_KEY.get(name) : undefined;

/**
 * Maps a list of certification UUIDs (e.g. `license.selected_certification_ids`)
 * to the settings keys used across the app (`bnb | dgnb18 | dgnb23 | levels | qng`).
 * Bridges via certification name: UUID -> name (certificationOptions) -> key (CertificationEnum).
 * Kept in utils (not in the hook) so non-hook store code can reuse it without a circular import.
 */
export const certificationIdsToKeys = (
  ids: string[] | undefined | null,
  certificationOptions: { id: string; name: string }[]
): string[] => {
  if (!ids?.length || !certificationOptions.length) return [];
  const idToName = new Map(certificationOptions.map((c) => [c.id, c.name]));
  return ids
    .map((id) => idToName.get(id))
    .map((name) => certificationNameToKey(name))
    .filter((key): key is string => Boolean(key));
};

export const GWP_M2_AREA_UNIT = (
  <span>
    kgCO<sub>2</sub>e/m<sup>2</sup>
    <sub>NRF</sub> * a
  </span>
);

export const GWP_M2_UNIT = (
  <span>
    kgCO<sub>2</sub>e/m<sup>2</sup>
    <sub>NRF</sub>
  </span>
);

export const GWP_UNIT = (
  <span>
    kgCO<sub>2</sub>e
  </span>
);

export const PENRT_UNIT = (
  <span>
    kWh/m<sup>2</sup>
    <sub>NRF</sub> * a
  </span>
);

export const KG_M2_UNIT = (
  <span>
    kg/m<sup>2</sup>
    <sub>NRF</sub>
  </span>
);

export const M2_NRF_UNIT = (
  <span>
    m<sup>2</sup>
    <sub>NRF</sub>
  </span>
);

export const M2_BGF_UNIT = (
  <span>
    m<sup>2</sup>
    <sub>BGF</sub>
  </span>
);

export const M2_NGF_UNIT = (
  <span>
    m<sup>2</sup>
    <sub>NGF</sub>
  </span>
);

export const M2_DGNB_UNIT = (
  <span>
    m<sup>2</sup>
    <sub>DGNB</sub>
  </span>
);

export interface GroupCategory {
  id: string;
  name: string;
  values: string[];
}

export const ENVIRONMENTAL_CATEGORIES: GroupCategory[] = [
  { id: "1", name: "A1-A3", values: ["A1", "A2", "A3", "A1-A3"] },
  { id: "2", name: "A4-A5", values: ["A4", "A5"] },
  {
    id: "3",
    name: "B1-B7",
    values: ["B1", "B2", "B3", "B4", "B5", "B6", "B7"],
  },
  { id: "4", name: "C1-C4", values: ["C1", "C2", "C3", "C4"] },
  { id: "5", name: "D", values: ["D"] },
];

export const IconBubbleTooltip = (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9.05783 1.79297C9.95518 1.79353 10.8329 2.05735 11.5818 2.55176C12.3306 3.04616 12.9177 3.74975 13.2707 4.57471C13.8684 4.64209 14.4472 4.82646 14.9736 5.11743C15.4999 5.40838 15.9639 5.80006 16.3388 6.27026C16.7138 6.74053 16.9927 7.28019 17.1592 7.85815C17.3256 8.43602 17.3766 9.0411 17.3093 9.63867C17.2419 10.2363 17.0575 10.8152 16.7666 11.3416C16.4756 11.868 16.0833 12.3318 15.613 12.7068C15.1428 13.0817 14.6031 13.3606 14.0251 13.5271C13.4472 13.6935 12.8422 13.7446 12.2446 13.6772C11.8096 14.4466 11.1108 15.0326 10.2773 15.3267C9.44394 15.6207 8.53232 15.6028 7.71091 15.2769L4.42087 17.25V14.5591C4.08481 14.4814 3.77055 14.3284 3.50168 14.1123C3.2329 13.8962 3.01629 13.6224 2.86814 13.311C2.71995 12.9996 2.64425 12.6584 2.64621 12.3135C2.64822 11.9686 2.72744 11.6285 2.87912 11.3188C2.05951 10.8473 1.42702 10.1082 1.08762 9.22559C0.748221 8.34274 0.72234 7.3696 1.01511 6.47022C1.3079 5.57082 1.9012 4.79927 2.69529 4.2854C3.48937 3.77154 4.43628 3.54682 5.37668 3.64819C5.80248 3.07264 6.35744 2.6051 6.9968 2.28296C7.63613 1.96084 8.34195 1.7927 9.05783 1.79297ZM7.85593 7.80396C7.38167 7.80396 6.9968 8.18882 6.9968 8.66309C6.99688 9.13728 7.38172 9.52148 7.85593 9.52148V12.0981C7.85609 12.5722 8.2403 12.9564 8.71433 12.9565H9.57346C10.0476 12.9565 10.4317 12.5722 10.4319 12.0981C10.4319 11.6239 10.0477 11.2391 9.57346 11.239V8.66309C9.57346 8.18882 9.18859 7.80396 8.71433 7.80396H7.85593ZM8.71433 4.36963C8.2402 4.36978 7.85593 4.75386 7.85593 5.22803C7.85593 5.7022 8.2402 6.08627 8.71433 6.08643C9.18859 6.08643 9.57346 5.70229 9.57346 5.22803C9.57346 4.75377 9.18859 4.36963 8.71433 4.36963Z"
      fill="#6B7280"
    />
  </svg>
);

export const USER_ROLES = [
  { id: 1, name: "Admin" },
  { id: 2, name: "User" },
  { id: 3, name: "Supporter" },
];

export const IconPDF = (
  <svg
    width="64"
    height="64"
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18.6667 56H45.3334C48.2789 56 50.6667 53.6122 50.6667 50.6667V25.1046C50.6667 24.3973 50.3858 23.719 49.8857 23.219L35.4478 8.78105C34.9477 8.28095 34.2694 8 33.5621 8H18.6667C15.7212 8 13.3334 10.3878 13.3334 13.3333V50.6667C13.3334 53.6122 15.7212 56 18.6667 56Z"
      stroke="#6B7280"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M17.0913 42V31.2627H20.5703C21.8887 31.2627 22.748 31.3164 23.1484 31.4238C23.7637 31.585 24.2788 31.9365 24.6938 32.4785C25.1089 33.0156 25.3164 33.7114 25.3164 34.5659C25.3164 35.2251 25.1968 35.7793 24.9575 36.2285C24.7183 36.6777 24.4131 37.0317 24.042 37.2905C23.6758 37.5444 23.3022 37.7129 22.9214 37.7959C22.4038 37.8984 21.6543 37.9497 20.6729 37.9497H19.2593V42H17.0913ZM19.2593 33.0791V36.126H20.4458C21.3003 36.126 21.8716 36.0698 22.1597 35.9575C22.4478 35.8452 22.6724 35.6694 22.8335 35.4302C22.9995 35.1909 23.0825 34.9126 23.0825 34.5952C23.0825 34.2046 22.9678 33.8823 22.7383 33.6284C22.5088 33.3745 22.2183 33.2158 21.8667 33.1523C21.6079 33.1035 21.0879 33.0791 20.3066 33.0791H19.2593ZM27.0889 31.2627H31.0513C31.9448 31.2627 32.626 31.3311 33.0947 31.4678C33.7246 31.6533 34.2642 31.9829 34.7134 32.4565C35.1626 32.9302 35.5044 33.5112 35.7388 34.1997C35.9731 34.8833 36.0903 35.728 36.0903 36.7339C36.0903 37.6177 35.9805 38.3794 35.7607 39.019C35.4922 39.8003 35.1089 40.4326 34.6108 40.916C34.2349 41.2822 33.7271 41.5679 33.0874 41.7729C32.6089 41.9243 31.9692 42 31.1685 42H27.0889V31.2627ZM29.2568 33.0791V40.1909H30.8755C31.481 40.1909 31.918 40.1567 32.1865 40.0884C32.5381 40.0005 32.8286 39.8516 33.0581 39.6416C33.2925 39.4316 33.4829 39.0874 33.6294 38.6089C33.7759 38.1255 33.8491 37.4688 33.8491 36.6387C33.8491 35.8086 33.7759 35.1714 33.6294 34.7271C33.4829 34.2827 33.2778 33.936 33.0142 33.687C32.7505 33.438 32.416 33.2695 32.0107 33.1816C31.708 33.1133 31.1147 33.0791 30.231 33.0791H29.2568ZM37.9507 42V31.2627H45.3115V33.0791H40.1187V35.6206H44.6011V37.437H40.1187V42H37.9507Z"
      fill="#6B7280"
    />
  </svg>
);

export const IconCSVFile = (
  <svg
    width="64"
    height="64"
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18.6667 56H45.3334C48.2789 56 50.6667 53.6122 50.6667 50.6667V25.1046C50.6667 24.3973 50.3858 23.719 49.8857 23.219L35.4478 8.78105C34.9477 8.28095 34.2694 8 33.5621 8H18.6667C15.7212 8 13.3334 10.3878 13.3334 13.3333V50.6667C13.3334 53.6122 15.7212 56 18.6667 56Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M26.4307 36.4258L23.9961 36.8652C23.9141 36.3789 23.7266 36.0127 23.4336 35.7666C23.1465 35.5205 22.7715 35.3975 22.3086 35.3975C21.6934 35.3975 21.2012 35.6113 20.832 36.0391C20.4688 36.4609 20.2871 37.1699 20.2871 38.166C20.2871 39.2734 20.4717 40.0557 20.8408 40.5127C21.2158 40.9697 21.7168 41.1982 22.3438 41.1982C22.8125 41.1982 23.1963 41.0664 23.4951 40.8027C23.7939 40.5332 24.0049 40.0732 24.1279 39.4229L26.5537 39.8359C26.3018 40.9492 25.8184 41.79 25.1035 42.3584C24.3887 42.9268 23.4307 43.2109 22.2295 43.2109C20.8643 43.2109 19.7744 42.7803 18.96 41.9189C18.1514 41.0576 17.7471 39.8652 17.7471 38.3418C17.7471 36.8008 18.1543 35.6025 18.9688 34.7471C19.7832 33.8857 20.8848 33.4551 22.2734 33.4551C23.4102 33.4551 24.3125 33.7012 24.9805 34.1934C25.6543 34.6797 26.1377 35.4238 26.4307 36.4258ZM27.4414 40.3369L29.9199 39.959C30.0254 40.4395 30.2393 40.8057 30.5615 41.0576C30.8838 41.3037 31.335 41.4268 31.915 41.4268C32.5537 41.4268 33.0342 41.3096 33.3564 41.0752C33.5732 40.9111 33.6816 40.6914 33.6816 40.416C33.6816 40.2285 33.623 40.0732 33.5059 39.9502C33.3828 39.833 33.1074 39.7246 32.6797 39.625C30.6875 39.1855 29.4248 38.7842 28.8916 38.4209C28.1533 37.917 27.7842 37.2168 27.7842 36.3203C27.7842 35.5117 28.1035 34.832 28.7422 34.2812C29.3809 33.7305 30.3711 33.4551 31.7129 33.4551C32.9902 33.4551 33.9395 33.6631 34.5605 34.0791C35.1816 34.4951 35.6094 35.1104 35.8438 35.9248L33.5146 36.3555C33.415 35.9922 33.2246 35.7139 32.9434 35.5205C32.668 35.3271 32.2725 35.2305 31.7568 35.2305C31.1064 35.2305 30.6406 35.3213 30.3594 35.5029C30.1719 35.6318 30.0781 35.7988 30.0781 36.0039C30.0781 36.1797 30.1602 36.3291 30.3242 36.4521C30.5469 36.6162 31.3145 36.8477 32.627 37.1465C33.9453 37.4453 34.8652 37.8115 35.3867 38.2451C35.9023 38.6846 36.1602 39.2969 36.1602 40.082C36.1602 40.9375 35.8027 41.6729 35.0879 42.2881C34.373 42.9033 33.3154 43.2109 31.915 43.2109C30.6436 43.2109 29.6357 42.9531 28.8916 42.4375C28.1533 41.9219 27.6699 41.2217 27.4414 40.3369ZM40.8975 43L37.1357 33.666H39.7285L41.4863 38.4297L41.9961 40.0205C42.1309 39.6162 42.2158 39.3496 42.251 39.2207C42.333 38.957 42.4209 38.6934 42.5146 38.4297L44.29 33.666H46.8301L43.1211 43H40.8975Z"
      fill="currentColor"
    />
  </svg>
);

export const ProductActionMode = {
  Edit: "edit",
  Duplicate: "duplicate",
  Generate: "generate",
} as const;
